/**
 * Axios client + intercepteurs auth / refresh / CSRF.
 *
 * - JWT en cookies httpOnly (côté serveur).
 * - withCredentials: true pour envoyer les cookies same-origin.
 * - Sur 401, on tente un refresh automatique (queue anti-concurrence).
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";

import { env } from "@/config/env";
import { trackRequest } from "@/lib/api/connection-status";
import { confirmServerUnreachable } from "@/lib/api/server-reachability";
import { isTenantGone, leaveGoneTenant } from "@/lib/api/tenant-gone";
import { useSiteStore } from "@/features/sites/store";

// Fin de suivi « connexion lente » attachée à la requête (voir connection-status).
type TrackedConfig = { _endTrack?: () => void };

const csrfFromCookie = (): string | null => {
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? decodeURIComponent(match[1]!) : null;
};

/**
 * Plafond d'attente d'une requête JSON. Aucun appel JSON de l'application ne
 * dure une minute (la liste la plus lourde répond en ~200 ms) : passé ce délai
 * la connexion est morte, pas lente.
 *
 * Sans ce plafond, une connexion morte — interface réseau active, débit nul,
 * cas courant en clinique — laissait la promesse axios en suspens
 * INDÉFINIMENT : l'écran d'enregistrement restait figé sur son spinner, sans
 * erreur, sans sortie possible autre que recharger la page.
 *
 * Les téléchargements longs (exports PDF/Excel) en sont exemptés plus bas :
 * eux ont légitimement droit aux 300 s accordés par nginx et gunicorn.
 */
const REQUEST_TIMEOUT_MS = 60_000;

/**
 * Statuts émis par le proxy quand l'API n'est pas là pour répondre : la
 * requête n'a jamais atteint Django. Ce n'est pas un refus métier, c'est une
 * indisponibilité — et elle est presque toujours brève.
 *
 * Le déploiement de production suit la branche `prod-saas` par un cron qui
 * relance `docker compose up -d --build` (RUNBOOK-SAAS-PROD.md). Le conteneur
 * api redémarre alors, et son point d'entrée exécute migrations, migrations
 * de toutes les bases tenant, seed RBAC puis collectstatic AVANT d'ouvrir
 * gunicorn : une à deux minutes pendant lesquelles nginx répond 502 à tout —
 * enregistrement, connexion, listes, exports. L'utilisateur voyait une erreur
 * rouge sur n'importe quelle action, réessayait, et « ça finissait par
 * marcher ». Rejouer discrètement quelques secondes plus tard rend cette
 * fenêtre invisible au lieu de la faire porter à la clinique.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504]);

/**
 * Temporisations avant chaque nouvelle tentative, en millisecondes.
 *
 * Calées sur la durée RÉELLE du phénomène, pas sur une valeur ronde : au
 * redémarrage, le point d'entrée de l'api enchaîne migrations du control plane,
 * migrations de toutes les bases tenant, deux seeds RBAC et collectstatic avant
 * d'ouvrir gunicorn (`start_period: 60s` du healthcheck). Un rejeu de quelques
 * secondes ne couvrirait que la fin de cette fenêtre.
 *
 * Lectures : ~15 s d'attente cumulée, l'écran reste sur son état de chargement.
 * Écritures idempotentes : borne courte — l'utilisateur attend devant un
 * formulaire bloqué, mieux vaut lui rendre la main que le faire patienter.
 */
const GATEWAY_RETRY_DELAYS_READ_MS = [1_000, 2_000, 4_000, 8_000] as const;
const GATEWAY_RETRY_DELAYS_WRITE_MS = [1_000, 3_000] as const;

/** Méthodes sans effet de bord : les rejouer ne peut rien créer en double. */
const SAFE_METHODS = new Set(["get", "head", "options"]);

const wait = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export const api: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL,
  withCredentials: true,
  timeout: REQUEST_TIMEOUT_MS,
  headers: { "Content-Type": "application/json" },
});

/**
 * Config axios portant l'en-tête `Idempotency-Key` (créations offline-first).
 * La même clé, rejouée au retour du réseau, est dédoublonnée côté serveur.
 * Retourne `undefined` (aucune config) quand aucune clé n'est fournie.
 */
export const withIdempotencyKey = (
  key?: string,
): AxiosRequestConfig | undefined =>
  key ? { headers: { "Idempotency-Key": key } } : undefined;

api.interceptors.request.use((config) => {
  // Exports PDF/Excel : génération légitimement longue (nginx leur accorde
  // 300 s). Ils sont reconnaissables à `responseType: "blob"` — seul marqueur
  // commun aux 11 points d'appel — et gardent une attente illimitée.
  if (config.responseType === "blob") {
    config.timeout = 0;
  }
  const csrf = csrfFromCookie();
  if (csrf && ["post", "put", "patch", "delete"].includes((config.method || "").toLowerCase())) {
    config.headers["X-CSRFToken"] = csrf;
  }
  // Filtre « siège » par site : ignoré côté serveur pour un utilisateur déjà
  // borné à un site (le claim JWT prime), donc sans risque d'élargissement.
  const siteId = useSiteStore.getState().activeSiteId;
  if (siteId) {
    config.headers["X-Site-Id"] = siteId;
  }
  // Démarre le suivi « connexion lente » ; la fin est appelée à la réponse.
  (config as typeof config & TrackedConfig)._endTrack = trackRequest();
  return config;
});

/** Lecture d'un en-tête de requête, que `headers` soit un objet nu ou un
 *  `AxiosHeaders` (axios 1.x utilise l'un ou l'autre selon le point d'entrée). */
const headerValue = (config: AxiosRequestConfig | undefined, name: string): string | undefined => {
  const headers = config?.headers as
    | { get?: (n: string) => unknown; [key: string]: unknown }
    | undefined;
  const raw = typeof headers?.get === "function" ? headers.get(name) : headers?.[name];
  return typeof raw === "string" && raw ? raw : undefined;
};

// Anti-concurrence pour le refresh
let isRefreshing = false;
let pending: Array<(value?: unknown) => void> = [];

const flushPending = (error?: unknown): void => {
  pending.forEach((cb) => cb(error));
  pending = [];
};

api.interceptors.response.use(
  (response) => {
    (response.config as typeof response.config & TrackedConfig)._endTrack?.();
    return response;
  },
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & {
      _retry?: boolean;
      _idemRetry?: boolean;
      _gatewayRetries?: number;
    } & TrackedConfig;
    // Fin du suivi « connexion lente » pour cette tentative (un retry en
    // redémarre un nouveau via l'intercepteur de requête).
    original?._endTrack?.();

    // Aucune réponse (délai dépassé, coupure en cours d'envoi) : l'état de
    // l'écriture est INCONNU côté client — le serveur l'a peut-être enregistrée.
    // On rejoue UNE fois, mais seulement si la requête porte une clé
    // d'idempotence : le serveur renvoie alors sa première réponse au lieu de
    // créer un doublon (cf. IdempotentCreateMixin). Sans clé, on ne rejoue
    // JAMAIS — un second envoi créerait un second acte dans le dossier.
    if (
      !error.response &&
      original &&
      !original._idemRetry &&
      headerValue(original, "Idempotency-Key")
    ) {
      original._idemRetry = true;
      return api(original);
    }

    // Passerelle indisponible (API en cours de redémarrage) : la requête n'a
    // pas été traitée. On rejoue après une courte attente, sans rien montrer.
    //
    // Rejeu réservé à ce qui ne peut pas créer de doublon : les méthodes sûres
    // (GET/HEAD/OPTIONS), et les écritures porteuses d'une clé d'idempotence,
    // que le serveur dédoublonne (cf. IdempotentCreateMixin). Une écriture sans
    // clé n'est JAMAIS rejouée — même certain qu'elle n'a pas abouti, la règle
    // reste celle du dossier médical : jamais deux actes pour un seul geste.
    if (error.response && GATEWAY_STATUSES.has(error.response.status) && original) {
      const method = (original.method || "get").toLowerCase();
      const isSafe = SAFE_METHODS.has(method);
      const replayable = isSafe || Boolean(headerValue(original, "Idempotency-Key"));
      const schedule = isSafe ? GATEWAY_RETRY_DELAYS_READ_MS : GATEWAY_RETRY_DELAYS_WRITE_MS;
      const attempt = original._gatewayRetries ?? 0;
      const delay = schedule[attempt];
      if (replayable && delay !== undefined) {
        original._gatewayRetries = attempt + 1;
        await wait(delay);
        return api(original);
      }
      // Budget épuisé (ou requête non rejouable) : l'absence dépasse ce que le
      // rejeu sait masquer. On demande son avis au serveur avant de conclure —
      // un 504 isolé peut n'être qu'un export trop long. Confirmée, l'absence
      // bascule l'application hors ligne : les créations offline-capables
      // partent en file au lieu d'échouer.
      await confirmServerUnreachable();
    }

    // Aucune réponse alors que le poste est en ligne : nginx lui-même est
    // absent (le cas passerelle ci-dessus suppose qu'il a répondu). Même
    // verdict à demander, sinon cette panne-là resterait une erreur nue.
    if (!error.response && original) {
      await confirmServerUnreachable();
    }

    // Les endpoints d'authentification portent leur propre sémantique de 401
    // (identifiants invalides, compte verrouillé, session expirée). Un 401 ici
    // NE doit PAS déclencher un refresh : sinon l'échec du refresh (« Pas de
    // refresh token. ») masque le vrai message serveur et déclenche une
    // redirection parasite. On laisse donc l'erreur d'origine remonter telle quelle.
    const url = original?.url ?? "";
    const isAuthEndpoint = /\/auth\/(login|refresh|logout|onboarding-login|support-login)/.test(url);

    if (error.response?.status === 401 && original && !original._retry && !isAuthEndpoint) {
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pending.push((err) => (err ? reject(err) : resolve(api(original))));
        });
      }

      isRefreshing = true;
      try {
        await axios.post(
          `${env.VITE_API_URL}/auth/refresh/`,
          {},
          { withCredentials: true },
        );
        isRefreshing = false;
        flushPending();
        return api(original);
      } catch (refreshError) {
        isRefreshing = false;
        flushPending(refreshError);
        // Déconnecter UNIQUEMENT sur un verdict d'authentification. Un refresh
        // qui échoue parce que le serveur est injoignable ou en train de
        // redémarrer (502/503/504) ne dit rien de la session : la déconnexion
        // faisait perdre l'écran en cours — et la saisie non enregistrée — pour
        // une indisponibilité de quelques secondes. Même principe que
        // `network-error.ts` : « le serveur a refusé » n'est pas « le serveur
        // est injoignable ».
        const refreshStatus = (refreshError as AxiosError)?.response?.status;
        const sessionRejected = refreshStatus === 401 || refreshStatus === 403;
        if (
          sessionRejected &&
          typeof window !== "undefined" &&
          !window.location.pathname.startsWith("/login")
        ) {
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      }
    }
    // Espace supprimé ou suspendu : le serveur refuse tout, la session locale
    // n'a plus d'objet. Voir lib/api/tenant-gone.ts.
    if (isTenantGone(error)) {
      leaveGoneTenant();
      return Promise.reject(error);
    }

    // Refus de suppression pour cause de dépendances : si le compte est un
    // super éditeur GTS, lui proposer la cascade. Effet de bord volontaire —
    // l'erreur continue de remonter telle quelle à l'appelant, qui garde son
    // message. Voir features/cascade/offer.ts.
    // Import différé : `features/cascade/offer` réutilise ce même client
    // axios, et un import statique refermerait le cycle avant que `api` ne
    // soit construit.
    void import("@/features/cascade/offer").then((m) => m.offerCascadeIfAllowed(error));

    return Promise.reject(error);
  },
);
